import { useEffect, memo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ClockIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import useAdminTableState from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'

const AdminSightingsSection = memo(function AdminSightingsSection({ onBack }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()
  const [selectedIds, setSelectedIds] = useState(new Set())

  const {
    data: sightings,
    loading,
    error,
    searchInput,
    search,
    filter,
    pagination,
    handleSearchChange,
    handleFilterChange,
    handlePageChange,
    refetch
  } = useAdminTableState(
    async (params) => {
      return execute(async () => {
        // Hook provides params with: { page, limit, search?, filter? }
        const apiParams = { ...params }
        
        // Map filter to sighting-specific params
        if (params.filter === 'needs-curation') {
          apiParams.needsCuration = true
          delete apiParams.filter
        } else if (params.filter === 'pending-moderation') {
          apiParams.moderationStatus = 'pending'
          delete apiParams.filter
        } else if (params.filter && params.filter !== 'all') {
          delete apiParams.filter
        }
        
        const response = await adminAPI.getAllSightings(apiParams)
        return {
          data: response.data?.data || [],
          pagination: {
            page: response.data?.pagination?.page || 1,
            totalPages: response.data?.pagination?.totalPages || 1,
            total: response.data?.pagination?.total || 0
          }
        }
      })
    },
    { pageSize: 20 }
  )

  const filterOptions = [
    { value: 'all', label: 'All Sightings' },
    { value: 'needs-curation', label: 'Needs Curation' },
    { value: 'pending-moderation', label: 'Pending Moderation' }
  ]

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(sightings.map(s => s.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [sightings])

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Delete ${selectedIds.size} sighting(s)?`)) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => adminAPI.deleteSighting(id)))
      setSelectedIds(new Set())
      refetch()
    }, { successMessage: `${selectedIds.size} sighting(s) deleted successfully` })
  }, [selectedIds, refetch, execute])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Sightings Management</h2>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin/sightings/new')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 text-sm font-medium"
          >
            + Create Sighting
          </button>
          <button
            onClick={onBack}
            className="text-sm text-primary hover:text-primary-600"
          >
            ← Back to Overview
          </button>
        </div>
      </div>

      <AdminTableControls
        searchValue={searchInput}
        searchPlaceholder="Search entity name or location..."
        onSearchChange={handleSearchChange}
        filterValue={filter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemLabel="sightings"
        onPageChange={handlePageChange}
      />

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="px-4 lg:px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.size} item(s) selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 lg:px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={sightings.length > 0 && selectedIds.size === sightings.length}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirmations</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-gray-500">{t('app.loading')}</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-red-500">{error}</td>
              </tr>
            ) : sightings.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-gray-500">No sightings found</td>
              </tr>
            ) : (
              sightings.map((sighting) => (
                <tr 
                  key={sighting.id}
                  onClick={() => navigate(`/admin/sightings/${sighting.id}`)}
                  className={`hover:bg-gray-50 cursor-pointer ${sighting.needsCuration ? 'bg-amber-50' : ''} ${selectedIds.has(sighting.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(sighting.id)}
                      onChange={() => handleSelectOne(sighting.id)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {sighting.needsCuration && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <ClockIcon className="h-3 w-3" />
                          {sighting.pendingConfirmations} pending
                        </span>
                      )}
                      
                      {sighting.moderationStatus === 'pending' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <ClockIcon className="h-3 w-3" />
                          Moderation
                        </span>
                      )}
                      {sighting.moderationStatus === 'approved' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-3 w-3" />
                          Approved
                        </span>
                      )}
                      {sighting.moderationStatus === 'rejected' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="h-3 w-3" />
                          Rejected
                        </span>
                      )}
                      
                      {!sighting.moderationStatus && !sighting.needsCuration && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Unknown
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {sighting.entityName || 'Unknown Entity'}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {sighting.address || `${sighting.latitude?.toFixed(4)}, ${sighting.longitude?.toFixed(4)}`}
                    </div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      sighting.sightingType === 'checkin' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {sighting.sightingType}
                    </span>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{sighting.confirmationCount || 0} approved</div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(sighting.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
})

export default AdminSightingsSection
